import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {onAuthStateChanged, User} from 'firebase/auth';
import {auth} from '@src/firebase';
import {useLazyGetMemberByUsernameQuery} from '@src/Api/TraceApi/traceApi';
import useFireBaseAuthentication from '@src/Hooks/useFirebaseAuthentication';
import {useAppDispatch, useAppSelector} from '@src/Store/hooks';
import useNavigation from '@src/Navigation/useNavigation';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import InitTrackerUser from './InitTrackerUser/InitTrackerUser';
import {useSession} from '../SessionProvider';
import {useAnonymousAuth} from '../AnonymousAuthProvider/AnonymousAuthProvider';

// Promesse globale partagée pour l'initialisation de session
// Cela garantit que les appels à onAuthStateChanged qui arrivent en même temps utiliseront la même promesse
let globalSessionInitPromise: Promise<boolean> | null = null;
let isGlobalSessionInitializing = false;
// Add a flag to track manual session initialization from sign-in function
let sessionManuallyInitialized = false;

type AuthState = {
  isLoggedIn: boolean;
  isLoading: boolean;
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  checkAuth: (user: User | null) => Promise<void>;
  setSessionManuallyInitialized: (value: boolean) => void;
};

export const AuthContext = createContext<AuthState>({
  isLoggedIn: false,
  isLoading: true,
  setIsLoggedIn: () => {},
  checkAuth: () => Promise.resolve(),
  setSessionManuallyInitialized: () => {},
});

const AuthProvider = ({children}: {children: React.ReactElement}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [fetchMemberByUsernameAndUpdateStore] =
    useLazyGetMemberByUsernameQuery();
  const {signOut, handleNavigation} = useFireBaseAuthentication();
  const {authentificationType, socialNetworkSource, openAuthenticationModal} =
    useAppSelector(state => state.appAuth);
  const rootNavigation = useNavigation();
  const dispatch = useAppDispatch();
  const session = useSession();
  const {isAnonymous, user: anonymousUser, isInitialized} = useAnonymousAuth();

  // Create refs to store the latest values
  const authTypeRef = useRef(authentificationType);
  const socialSourceRef = useRef(socialNetworkSource);

  // Add new ref for redirect URL
  const redirectUrlRef = useRef<string | undefined>(undefined);

  // Add ref to track if session has been initialized
  const sessionInitializedRef = useRef(false);

  // Update refs whenever the values change
  useEffect(() => {
    authTypeRef.current = authentificationType;
    socialSourceRef.current = socialNetworkSource;
    // Store redirect URL in ref when modal state changes
    redirectUrlRef.current = openAuthenticationModal?.redirect;
  }, [authentificationType, socialNetworkSource, openAuthenticationModal]);

  const checkAuth = useCallback(async (user: User | null) => {
    const handleNotLoggedIn = async () => {
      setIsLoggedIn(false);
      await signOut();
      redirectUrlRef.current = undefined;
      // Reset session initialized flag when user logs out
      sessionInitializedRef.current = false;
      // Reset global initialization state
      isGlobalSessionInitializing = false;
      globalSessionInitPromise = null;
      // Reset manual initialization flag
      sessionManuallyInitialized = false;
    };

    try {
      if (
        authTypeRef.current === 'register' &&
        socialSourceRef.current !== 'email' &&
        user &&
        user.email
      ) {
        setIsLoggedIn(false);
      } else if (user && (user.email || user.isAnonymous)) {
        // Pour les utilisateurs anonymes, on considère qu'ils sont "logés" pour l'accès au catalogue
        if (user.isAnonymous) {
          setIsLoggedIn(true);
          setIsLoading(false);
          return;
        }

        // Check if session is already initialized manually from signIn function
        if (sessionManuallyInitialized) {
          // We don't set isLoggedIn=true here anymore, as that's handled by the signin process
          // Just mark that session is initialized to avoid duplicate initialization
          sessionInitializedRef.current = true;

          // We don't handle navigation here as it will be handled by the signin process
          // after it completes successfully

          // Still set isLoading=false so the UI can respond
          setIsLoading(false);
          return;
        }

        // Check if session is already initialized
        if (sessionInitializedRef.current) {
          setIsLoading(false);
          return;
        }

        // Check for global session initialization in progress
        if (isGlobalSessionInitializing && globalSessionInitPromise) {
          // Wait for the existing initialization to complete
          const result = await globalSessionInitPromise;
          if (result) {
            // If successful, set our local initialized flag
            sessionInitializedRef.current = true;
            // Continue with member data check after waiting
            await checkUserMemberData(user);
          } else {
            await handleNotLoggedIn();
          }
          setIsLoading(false);
          return;
        }

        // Set global flags to prevent concurrent initializations
        isGlobalSessionInitializing = true;
        globalSessionInitPromise = session.initializeSession();

        try {
          // Wait for session initialization to complete
          const isSuccessed = await globalSessionInitPromise;

          // If initialization is successful, mark session as initialized and proceed
          if (isSuccessed) {
            sessionInitializedRef.current = true;
            await checkUserMemberData(user);
          } else {
            // If initialization failed, reset flags and handle not logged in
            sessionInitializedRef.current = false;
            await handleNotLoggedIn();
          }
        } catch (error) {
          await handleNotLoggedIn();
        } finally {
          // Reset global initialization state
          isGlobalSessionInitializing = false;
          globalSessionInitPromise = null;
        }
      } else {
        await handleNotLoggedIn();
      }
    } catch (error) {
      // Reset global initialization state
      isGlobalSessionInitializing = false;
      globalSessionInitPromise = null;
      // Reset manual initialization flag
      sessionManuallyInitialized = false;
      await handleNotLoggedIn();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper function to check user member data and handle navigation
  const checkUserMemberData = async (user: User) => {
    try {
      const {data: memberData} = await fetchMemberByUsernameAndUpdateStore();

      if (
        memberData &&
        memberData.email &&
        user.email &&
        memberData.email.toLowerCase() === user.email.toLowerCase()
      ) {
        setIsLoggedIn(true);

        // Handle navigation
        await handleNavigation();
      } else {
        sessionInitializedRef.current = false;
        setIsLoggedIn(false);
        signOut();
      }
    } catch (error) {
      sessionInitializedRef.current = false;
      setIsLoggedIn(false);
      signOut();
    }
  };

  // Implement check for user status here (e.g., check tokens)
  useEffect(() => {
    // Reset session flag when component mounts
    sessionInitializedRef.current = false;
    // Reset global state when component mounts
    isGlobalSessionInitializing = false;
    globalSessionInitPromise = null;

    const unsub = onAuthStateChanged(auth, checkAuth);

    return () => {
      setIsLoggedIn(false);
      setIsLoading(true);
      sessionInitializedRef.current = false;
      // Reset global state when component unmounts
      isGlobalSessionInitializing = false;
      globalSessionInitPromise = null;
      unsub();
    };
  }, [checkAuth]);

  // Function to set the manual initialization flag
  const setSessionManuallyInitialized = useCallback((value: boolean) => {
    sessionManuallyInitialized = value;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isLoading,
        setIsLoggedIn,
        checkAuth: checkAuth as (user: User | null) => Promise<void>,
        setSessionManuallyInitialized,
      }}>
      <InitTrackerUser />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
